import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldStarIcon, XIcon, UserPlusIcon } from "@phosphor-icons/react";

/**
 * Seller-only "Manage moderators" panel for a single live stream.
 *
 * Promotes any signed-in user (currently watching or by handle) to
 * stream-moderator. Stream mods get the same chat actions as the seller:
 * mute / unmute, kick, delete / hide / pin chat messages — but only
 * inside this stream.
 *
 * Hard-capped at 5 mods per stream by the backend.
 */
export default function StreamModManager({ streamId, onClose }) {
  const [mods, setMods] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [m, v] = await Promise.all([
        api.get(`/streams/${streamId}/mods`),
        api.get(`/streams/${streamId}/viewers`).catch(() => ({ data: { viewers: [] } })),
      ]);
      setMods(m.data.items || []);
      setViewers(v.data.viewers || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  const promote = async (userId, label) => {
    setBusy(userId);
    try {
      await api.post(`/streams/${streamId}/mods`, { user_id: userId });
      toast.success(`${label || "User"} is now a moderator`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (userId, label) => {
    setBusy(userId);
    try {
      await api.delete(`/streams/${streamId}/mods/${userId}`);
      toast.success(`Removed ${label || "user"} as moderator`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const modIds = new Set(mods.map((m) => m.user_id));
  const candidates = (viewers || [])
    .filter((v) => v.user_id && !modIds.has(v.user_id))
    .filter((v) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (v.username || "").toLowerCase().includes(q) ||
        (v.name || "").toLowerCase().includes(q) ||
        (v.user_name || "").toLowerCase().includes(q)
      );
    })
    .slice(0, 30);

  return (
    <div
      className="absolute inset-0 z-30 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="stream-mod-manager"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-4 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <ShieldStarIcon size={18} className="text-[#1D78AB]" />
            <div>
              <p className="font-display font-bold">Stream moderators</p>
              <p className="text-[11px] text-zinc-500 font-mono uppercase tracking-wider">
                Up to 5 per show · {mods.length}/5 used
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-7 w-7"
            data-testid="stream-mod-manager-close"
          >
            <XIcon size={14} />
          </Button>
        </header>

        <section className="p-4 space-y-3">
          <p className="overline text-zinc-500">CURRENT MODS</p>
          {loading ? (
            <div className="text-sm text-zinc-500 py-4">Loading…</div>
          ) : mods.length === 0 ? (
            <div className="text-sm text-zinc-500 italic py-2">
              No moderators yet. Pick someone you trust below.
            </div>
          ) : (
            <ul className="space-y-2">
              {mods.map((m) => (
                <li
                  key={m.user_id}
                  className="flex items-center gap-2.5 p-2 rounded-lg bg-zinc-50 border border-zinc-200"
                >
                  <div className="h-8 w-8 rounded-full bg-zinc-200 overflow-hidden flex-shrink-0">
                    {m.profile?.picture ? (
                      <img
                        src={m.profile.picture}
                        alt={m.profile.username}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full grid place-items-center text-xs font-bold text-zinc-500">
                        {(m.profile?.username || m.profile?.name || "?")[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">
                      @{m.profile?.username || m.profile?.name || m.user_id}
                    </div>
                    <div className="text-[11px] text-zinc-400 font-mono">
                      Mod since {new Date(m.granted_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => revoke(m.user_id, m.profile?.username || m.profile?.name)}
                    disabled={busy === m.user_id}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`stream-mod-revoke-${m.user_id}`}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="p-4 border-t border-zinc-200 space-y-3">
          <p className="overline text-zinc-500">APPOINT FROM VIEWERS</p>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by @handle or name…"
            className="h-9 text-sm"
            data-testid="stream-mod-search"
          />
          {candidates.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">
              {viewers && viewers.length > 0
                ? "No viewers match that search."
                : "No viewers in the stream right now. Once someone joins, you can promote them here."}
            </p>
          ) : (
            <ul className="space-y-1.5 max-h-56 overflow-y-auto">
              {candidates.map((v) => (
                <li
                  key={v.user_id}
                  className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-zinc-50"
                >
                  <div className="h-7 w-7 rounded-full bg-zinc-200 overflow-hidden flex-shrink-0">
                    {v.picture ? (
                      <img src={v.picture} alt={v.user_name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full grid place-items-center text-[10px] font-bold text-zinc-500">
                        {(v.username || v.user_name || v.name || "?")[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">
                      @{v.username || v.user_name || v.name}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => promote(v.user_id, v.username || v.user_name)}
                    disabled={busy === v.user_id || mods.length >= 5}
                    className="bg-[#1D78AB] hover:bg-[#176089] text-white"
                    data-testid={`stream-mod-promote-${v.user_id}`}
                  >
                    <UserPlusIcon size={12} className="mr-1" /> Make mod
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
